/**
 * PChrome — shared full-profile chrome. Identity row + a Profile/Analytics
 * underline toggle (pushed right) + the shared 5-cell VitalStrip + ONE tab row
 * that regenerates by mode (NEVER two stacked bars). `mode` ('analytics'|'profile')
 * swaps ATabs(5) ↔ FTabs(6). 14px horizontal inset shared by every line; ~2px
 * VitalStrip→tabs gap. Themeable via var(--team-primary).
 */
import React from 'react';
import { dynastyTheme, withAlpha } from '../../../../../../services/colorService';
import PropTypes from 'prop-types';
import VitalStrip from '../../shared/VitalStrip';
import { ATabs, FTabs } from './FullProfileTabs';

const C = dynastyTheme.tokens.colors;
const GOLD = C.primary;
const OFF = C.mutedLight;
const INSET = '0 14px';

const initials = (name) => (name || '')
  .split(' ').filter(Boolean).map((p) => p[0]).join('').slice(0, 3).toUpperCase() || '—';

const ToggleLabel = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    role="tab"
    aria-selected={active}
    style={{ position: 'relative', border: 'none', background: 'transparent', padding: '0 0 3px', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
  >
    <span style={{ fontSize: 10, fontWeight: active ? 900 : 800, color: active ? GOLD : OFF }}>{label}</span>
    <span aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, background: active ? GOLD : 'transparent' }} />
  </button>
);

const PChrome = ({ player, mode, onModeChange, atab, ftab, onTabChange }) => {
  const info = player?.info || {};
  const name = info.full_name || 'Player';
  const pos = info.position || '';
  const team = info.mlb_team || info.team || '';
  const age = info.age != null ? `${info.age}y` : (info.birthdate ? '' : '');
  const teamAge = [team, age].filter(Boolean).join(' · ');
  const isHot = player?.analytics?.hot_cold?.status === 'hot';
  const isAnalytics = mode === 'analytics';

  return (
    <div style={{ flexShrink: 0 }}>
      {/* Identity row + Profile/Analytics toggle (right) */}
      <div style={{ padding: '0 14px 6px', display: 'flex', gap: 10, alignItems: 'center' }}>
        <div style={{
          width: 42, height: 42, borderRadius: 8, flexShrink: 0,
          background: `repeating-linear-gradient(135deg,${C.surface[840]},${C.surface[840]} 5px,${C.surface[815]} 5px,${C.surface[815]} 10px)`,
          border: `1px solid ${withAlpha(C.goldBase, 0.32)}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: 10, fontWeight: 900, color: GOLD }}>{initials(name)}</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 15, fontWeight: 900, color: C.white, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
            <span style={{ color: GOLD, fontSize: 10 }}>★</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
            {pos && (
              <span style={{ fontSize: 9, fontWeight: 800, color: GOLD, background: C.primaryTint.t13, border: `1px solid ${withAlpha(C.goldBase, 0.32)}`, borderRadius: 3, padding: '1px 5px' }}>{pos}</span>
            )}
            {teamAge && <span style={{ fontSize: 9.5, color: C.coolGray[150], fontWeight: 700 }}>{teamAge}</span>}
            {isHot && <span style={{ fontSize: 9, color: C.spark.down, fontWeight: 800 }}>▲ HOT</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 11, flexShrink: 0, alignItems: 'center' }}>
          <ToggleLabel label="Profile" active={!isAnalytics} onClick={() => onModeChange('profile')} />
          <ToggleLabel label="Analytics" active={isAnalytics} onClick={() => onModeChange('analytics')} />
        </div>
      </div>

      {/* VitalStrip (shared) */}
      <div style={{ padding: INSET, paddingBottom: 2 }}>
        <VitalStrip player={player} />
      </div>

      {/* ONE tab row, regenerates by mode */}
      <div style={{ padding: '0 14px 6px' }}>
        {isAnalytics
          ? <ATabs active={atab} onChange={(k) => onTabChange('analytics', k)} />
          : <FTabs active={ftab} onChange={(k) => onTabChange('profile', k)} />}
      </div>
    </div>
  );
};

PChrome.propTypes = {
  player: PropTypes.object,
  mode: PropTypes.oneOf(['analytics', 'profile']).isRequired,
  onModeChange: PropTypes.func.isRequired,
  atab: PropTypes.string,
  ftab: PropTypes.string,
  onTabChange: PropTypes.func.isRequired,
};

export default PChrome;
